/**
 * PATH: packages/email-hub/src/feature/reader-header/menus/menuScopes.ts
 * FILE_ID: EMAILHUB-READERHEADER-MENUS-SCOPES-001
 * PURPOSE: Name every context-menu scope (lane / chip) the reader header routes.
 * ROLE IN ARCHITECTURE: Pure vocabulary shared by routing, builders, and tests.
 * MAIN EXPORTS: ReaderContextLaneId, ReaderChipKind, ReaderMenuScope, LANE_MENU_OWNER.
 * NON-RESPONSIBILITIES: No DOM, no state, no menu items.
 * NOTES FOR FUTURE AI: Lane menus are GENERATED from the owner action's
 *   contextActions in actions/registry.ts (D269-02) — add ids there first.
 */
import type { ReaderActionId } from '../model/types'

/** The four semantic lanes of the expanded triage context (plan D269-03),
 *  plus the three workflow cells (change #269 P5) which reuse the same
 *  routing/menu machinery — each cell is its own scope so Owner/Due/Watchers
 *  menus stay independent. */
export type ReaderTriageLaneId =
  | 'company'
  | 'classification'
  | 'intelligence'
  | 'filedTo'

export type ReaderContextLaneId =
  | ReaderTriageLaneId
  | 'workflowOwner'
  | 'workflowDue'
  | 'workflowWatchers'

/** Workflow cell scopes — their menus render inside WorkflowRow, not the
 *  ExpandedContext lane grid. */
export const WORKFLOW_LANES: readonly ReaderContextLaneId[] = [
  'workflowOwner',
  'workflowDue',
  'workflowWatchers',
]

export function isWorkflowLane(lane: ReaderContextLaneId): boolean {
  return WORKFLOW_LANES.includes(lane)
}

/** Chips that bubble to a chip-scoped menu instead of the bare lane menu.
 *  Chips owning a full native menu (company, placement) are NOT listed:
 *  they stop propagation themselves and never reach the router. */
export type ReaderChipKind =
  | 'category'
  | 'verification'
  | 'invoice'
  | 'tag'
  | 'provenance'

export type ReaderMenuScope =
  | { kind: 'lane'; lane: ReaderContextLaneId }
  | { kind: 'chip'; lane: ReaderContextLaneId; chip: ReaderChipKind }

/** Registry action that OWNS each lane's bare menu (its contextActions). */
export const LANE_MENU_OWNER: Readonly<Record<ReaderContextLaneId, ReaderActionId>> = {
  company: 'manage_company',
  classification: 'manage_classification',
  intelligence: 'manage_signals',
  filedTo: 'manage_placements',
  workflowOwner: 'manage_owner',
  workflowDue: 'manage_due',
  workflowWatchers: 'manage_watchers',
}

/** Operator-facing lane headings (plan lane names, not legacy Who/What). */
export const LANE_MENU_HEADINGS: Readonly<Record<ReaderContextLaneId, string>> = {
  company: 'Company',
  classification: 'Classification',
  intelligence: 'Intelligence',
  filedTo: 'Filed to',
  workflowOwner: 'Owner',
  workflowDue: 'Due',
  workflowWatchers: 'Watchers',
}

/** DOM marker the router reads to resolve a chip scope from an event target. */
export const READER_CHIP_ATTR = 'data-reader-chip'
/** DOM marker identifying a lane root surface. */
export const READER_LANE_ATTR = 'data-reader-lane'
